import React from "react";
import { Divider, IconButton, Menu, Typography } from "@material-ui/core";
import { MoreVert } from "@material-ui/icons";
import MenuItem from "@material-ui/core/MenuItem";
import { store } from "@store";
import { InvoiceProvider, Tenant } from "@bonliva-crm/shared";
import { useCreateFortnoxVoucherMutation } from "@graphql";
import { useLocale, useSnackbars, useTenant } from "@hooks";
import VoucherFortnoxForm, { FormState } from "./voucher-fortnox-form";
import { useFormStore } from "@modules/formx-react/useFormStore";
import yup from "yup";

const initialValues = {
  date: new Date(),
  description: null,
  provider: null
} as FormState;

export default function SendVoucherFortnoxMenu() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<string>(null);
  const snack = useSnackbars();
  const { locale } = useLocale();
  const { tenant } = useTenant();

  const [createVoucher, { loading }] = useCreateFortnoxVoucherMutation({
    onError: (e) => {
      snack.showError(e.message);
    },
    onCompleted: (res) => {
      snack.showSuccess(locale.UnbookedInvoicesCreatedJob);
      setResult(res?.submitFortnoxVoucher?.result ?? null);
    }
  });

  const form = useFormStore<FormState>({
    schema: yup.object({
      date: yup
        .string()
        .nullable(true)
        .required(locale.fieldIsMandatory(locale.PostingDate)),
      description: yup
        .string()
        .nullable(true)
        .required(locale.fieldIsMandatory(locale.Description)),
      provider: yup.mixed().nullable(true)
    }),
    onSubmit: () => {
      return createVoucher({
        variables: {
          ...form.values
        }
      });
    },
    initialValues
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDialog = () => {
    setDialogOpen(!dialogOpen);
    setResult(null);
  };

  const handleMenuItemClick = (provider: InvoiceProvider) => {
    handleClose();
    toggleDialog();
    form.reset({ ...initialValues, provider });
  };

  const menuItems = [];

  if (tenant === Tenant.BonlivaAB) {
    menuItems.push(
      <MenuItem onClick={() => handleMenuItemClick(InvoiceProvider.Fortnox)}>
        {locale.tenants[Tenant.BonlivaAB]}
      </MenuItem>
    );
    menuItems.push(
      <MenuItem
        disabled // temporarily disabled
        onClick={() => handleMenuItemClick(InvoiceProvider.BonlivaCare)}
      >
        Bonliva Care
      </MenuItem>
    );
  }

  if (tenant === Tenant.WCC) {
    menuItems.push(
      <MenuItem onClick={() => handleMenuItemClick(InvoiceProvider.WCC)}>
        {locale.tenants[Tenant.WCC]}
      </MenuItem>
    );
  }

  return store().user.can("fortnox-voucher:create:all") ? (
    <>
      <IconButton aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu
        id="fortnox-voucher-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Typography style={{ padding: "0 16px" }}>
          {locale.CreateInvoiceVoucherFortnoxMenu}
        </Typography>
        <Divider />
        {menuItems.map((item) => item)}
      </Menu>
      <VoucherFortnoxForm
        form={form}
        loading={loading}
        onClose={toggleDialog}
        open={dialogOpen}
        result={result}
      />
    </>
  ) : null;
}
